(function () {
  const API_BASE = "https://apiv2.loomcustom.com/v1";
  const LOCAL_API_BASE = "http://localhost:8006/v1";
  const JOB_POLL_INTERVAL_MS = 2000;
  const JOB_POLL_TIMEOUT_MS = 30 * 60 * 1000;
  const MISSING_TEMPLATE_IMAGE_MESSAGE =
    "This option has no design preview image. Please choose another option or contact us for a custom design.";
  const SUPPORTED_UPLOAD_TYPES = new Set([
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/heic",
    "image/heif",
  ]);
  const HEIC_UPLOAD_TYPES = new Set(["image/heic", "image/heif"]);
  const UPLOAD_ACCEPT_ATTRIBUTE =
    "image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif";

  class JobPollingStoppedError extends Error {
    constructor() {
      super("Job polling stopped");
      this.name = "JobPollingStoppedError";
    }
  }

  // Session ID for gallery persistence (per-browser, no login required)
  function getSessionId() {
    const key = "ai_customizer_session_id";
    let id = localStorage.getItem(key);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(key, id);
    }
    return id;
  }
  const SESSION_ID = getSessionId();

  function getJobToken() {
    const key = "ai_customizer_job_token";
    let token = localStorage.getItem(key);
    if (!token) {
      const bytes = new Uint8Array(32);
      crypto.getRandomValues(bytes);
      token = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
      localStorage.setItem(key, token);
    }
    return token;
  }
  const JOB_TOKEN = getJobToken();

  function generateIdempotencyKey() {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }

  function unwrapPublicPayload(payload) {
    if (
      payload &&
      payload.success === true &&
      Object.prototype.hasOwnProperty.call(payload, "data")
    ) {
      return payload.data;
    }
    return payload;
  }

  function parseJSONAttr(raw) {
    if (!raw) return null;
    const trimmed = raw.trim();
    if (!trimmed || trimmed === "null") return null;
    try {
      let parsed = JSON.parse(trimmed);
      // Tolerate a double-encoded value: JSON pasted into a text-type
      // metafield gets re-serialized by Liquid's `json` filter into a JSON
      // *string*, so the first parse yields a string that is itself JSON.
      if (typeof parsed === "string") {
        parsed = JSON.parse(parsed);
      }
      return parsed;
    } catch {
      return null;
    }
  }

  function resolveApiBase(configuredApiBase) {
    const hostname = window.location.hostname;
    const apiBase = API_BASE;
      // hostname === "localhost" || hostname === "127.0.0.1"
      //   ? LOCAL_API_BASE
      //   : configuredApiBase || API_BASE;
    return apiBase.replace(/\/+$/, "");
  }

  class AiCustomizer {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.productHandle = container.dataset.productHandle;

      // A local Shopify Theme Dev page always talks to the local backend.
      // Remote previews continue to use the per-store Liquid configuration.
      this.apiBase = resolveApiBase(container.dataset.apiBase);

      // F1 — per-variant prompt routing.
      this.templateName = container.dataset.templateName || "";
      this.templateBase = this.templateName; // fallback when no map match
      this.templateMap = parseJSONAttr(container.dataset.templateMap); // {option, map} | null
      this.optionNames = parseJSONAttr(container.dataset.optionNames) || [];

      this.variants = [];
      this.templateImageUrl = null;
      this.configFields = [];

      // F2/F3 — customer images become a list of upload slots (0..N).
      this.uploads = []; // [{ file, url, el, input, placeholder, preview, previewImg }]
      this.portraitMin = 1;
      this.portraitMax = 1;

      // Race guards for config/gallery re-fetches on variant change.
      this.configFetchToken = 0;
      this.galleryFetchToken = 0;
      // Becomes true after the constructor's initial fetch is kicked off, so
      // a variant change before that doesn't double-fetch.
      this._ready = false;

      this.galleryImages = [];
      this.galleryOpen = false;
      this.resultContext = null;
      this.restoredJobContext = null;
      this.restoreInputsToken = 0;
      this.restoringJobId = null;
      this.activeJobId = null;
      this.isLoading = false;
      this.jobPollToken = 0;
      this.jobRunId = 0;
      this.jobProgressValue = 0;
      this.jobProgressInitialEta = null;

      this.initVariantTracking();
      this.cacheElements();
      this.bindEvents();
      this.loadStoredInputsContext();
      this.fetchConfig();
      this.fetchGallery();
      this.resumeLatestJob();
      this._ready = true;
    }

    jobHeaders(extra = {}) {
      return { "X-Job-Token": JOB_TOKEN, ...extra };
    }

    cacheElements() {
      const c = this.container;
      const id = this.sectionId;

      this.card = c.querySelector(`#AiCustomizerCard-${id}`);
      this.uploadsContainer = c.querySelector(`#AiCustomizerUploads-${id}`);
      this.submitBtn = c.querySelector(`#AiCustomizerSubmit-${id}`);
      this.submitText = c.querySelector(".ai-customizer__submit-text");
      this.submitLoading = c.querySelector(".ai-customizer__submit-loading");
      this.cancelBtn = c.querySelector(`#AiCustomizerCancel-${id}`);
      this.statusEl = c.querySelector(`#AiCustomizerStatus-${id}`);
      this.progressEl = this.statusEl?.querySelector(
        ".ai-customizer__progress",
      );
      this.progressBar = this.statusEl?.querySelector(
        ".ai-customizer__progress-bar",
      );
      this.progressValueEl = this.statusEl?.querySelector(
        ".ai-customizer__progress-value",
      );
      this.resultEl = c.querySelector(`#AiCustomizerResult-${id}`);
      this.resultImg = c.querySelector(`#AiCustomizerResultImg-${id}`);
      this.fieldsContainer = c.querySelector(`#AiCustomizerFields-${id}`);
      this.noteContainer = c.querySelector(`#AiCustomizerNoteContainer-${id}`);
      this.noteInput = c.querySelector(`#AiCustomizerNote-${id}`);
      this.galleryEl = c.querySelector(`#AiCustomizerGallery-${id}`);
      this.galleryToggle = c.querySelector(`#AiCustomizerGalleryToggle-${id}`);
      this.galleryCount = c.querySelector(`#AiCustomizerGalleryCount-${id}`);
      this.galleryGrid = c.querySelector(`#AiCustomizerGalleryGrid-${id}`);
    }

    getSectionEl() {
      return document.querySelector(`#MainProduct-${this.sectionId}`);
    }

    getMainSwiper() {
      const el = this.getSectionEl();
      if (!el) return null;
      const swiperEl = el.querySelector(".product__media-list .js-media-list");
      return swiperEl && swiperEl.swiper ? swiperEl.swiper : null;
    }

    getSubSwiper() {
      const el = this.getSectionEl();
      if (!el) return null;
      const swiperEl = el.querySelector(
        ".product__media-sublist.js-media-sublist",
      );
      return swiperEl && swiperEl.swiper ? swiperEl.swiper : null;
    }

    initVariantTracking() {
      const sectionEl = this.getSectionEl();
      if (!sectionEl) return;

      // Load variant data from embedded JSON in variant-radios
      const variantRadios = sectionEl.querySelector("variant-radios");
      const variantSelects = sectionEl.querySelector("variant-selects");
      const variantEl = variantRadios || variantSelects;

      if (variantEl) {
        const script = variantEl.querySelector(
          'script[type="application/json"]',
        );
        if (script) {
          this.variants = JSON.parse(script.textContent);
          console.log(
            "[AiCustomizer] Loaded",
            this.variants.length,
            "variants",
          );
        }
      }

      // Set initial template from current variant (no re-fetch yet)
      this.updateTemplateFromCurrentVariant();

      const form = sectionEl.querySelector(
        'form[data-type="add-to-cart-form"]',
      );
      if (form) {
        const idInput = form.querySelector('input[name="id"]');
        if (idInput) {
          // Deterministic signal: VariantSelects dispatches a `change` on the
          // hidden id input AFTER writing its new value (assets/global.js).
          idInput.addEventListener("change", () => {
            this.updateTemplateFromCurrentVariant();
          });
          // Belt-and-suspenders: also catch attribute mutations.
          const observer = new MutationObserver(() => {
            this.updateTemplateFromCurrentVariant();
          });
          observer.observe(idInput, {
            attributes: true,
            attributeFilter: ["value"],
          });
        }
      }

      // Also listen for radio/select changes directly
      if (variantEl) {
        variantEl.addEventListener("change", () => {
          setTimeout(() => this.updateTemplateFromCurrentVariant(), 50);
        });
      }
    }

    // Resolve the prompt template_name for a given variant using the optional
    // template_map; falls back to the product-level base name.
    resolveTemplateName(variant) {
      const map = this.templateMap;
      if (!map || !map.option || !map.map) return this.templateBase;

      const idx = this.optionNames.findIndex(
        (n) => String(n).toLowerCase() === String(map.option).toLowerCase(),
      );
      if (idx < 0) return this.templateBase;

      const value = variant[`option${idx + 1}`];
      if (!value) return this.templateBase;

      return map.map[value] || this.templateBase;
    }

    updateTemplateFromCurrentVariant() {
      const sectionEl = this.getSectionEl();
      if (!sectionEl) return;

      const form = sectionEl.querySelector(
        'form[data-type="add-to-cart-form"]',
      );
      if (!form) return;

      const idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;

      const variantId = parseInt(idInput.value, 10);
      const variant = this.variants.find((v) => v.id === variantId);
      if (!variant) {
        const hadSource =
          this.currentVariantId != null || this.templateImageUrl != null;
        this.currentVariantId = null;
        this.templateImageUrl = null;
        if (this._ready && hadSource) this.onTemplateChanged();
        return;
      }

      const previousVariantId = this.currentVariantId;
      const previousImageUrl = this.templateImageUrl;
      // A variant without an image must never inherit another variant's design.
      let nextImageUrl = null;
      if (variant.featured_image && variant.featured_image.src) {
        let src = variant.featured_image.src;
        if (src.startsWith("//")) src = "https:" + src;
        nextImageUrl = src;
      }

      const resolved = this.resolveTemplateName(variant);
      const variantChanged =
        previousVariantId !== variant.id || previousImageUrl !== nextImageUrl;
      if (resolved && (resolved !== this.templateName || variantChanged)) {
        this.templateName = resolved;
        this.templateImageUrl = nextImageUrl;
        this.currentVariantId = variant.id;
        console.log("[AiCustomizer] Template →", resolved);
        // Re-fetch and invalidate for either prompt or source-image changes.
        if (this._ready) this.onTemplateChanged();
      } else {
        this.templateImageUrl = nextImageUrl;
        this.currentVariantId = variant.id;
      }
    }

    // Reset per-template state and re-fetch config + gallery for the new
    // template. Visibility is decided by fetchConfig (reveal on 200).
    onTemplateChanged() {
      // Portrait uploads are customer-owned inputs, not template outputs.
      // Carry ready uploads into the new variant/template-image context so a
      // character change never forces the customer to upload the same photo.
      const readyUploadIds = this.getReadyUploadIds();
      const carriedPortraitUploadIds =
        readyUploadIds.length > 0
          ? readyUploadIds
          : Array.isArray(this.restoredJobContext?.portraitUploadIds)
            ? [...this.restoredJobContext.portraitUploadIds]
            : [];
      this.jobRunId++;
      this.jobPollToken++;
      this.activeJobId = null;
      this.setLoading(false);
      this.restoredJobContext = null;
      this.restoreInputsToken++;
      this.restoringJobId = null;
      this.loadStoredInputsContext();
      if (carriedPortraitUploadIds.length > 0) {
        const restoredFieldValues = this.restoredJobContext?.fieldValues || {};
        this.restoreInputsToken++;
        this.restoredJobContext = {
          jobId: null,
          fieldValues: restoredFieldValues,
          portraitUploadIds: carriedPortraitUploadIds,
        };
        this.storeInputs(restoredFieldValues, carriedPortraitUploadIds);
      }
      this.configFields = [];
      this.uploads = [];
      if (this.uploadsContainer) this.uploadsContainer.innerHTML = "";
      if (this.fieldsContainer) {
        this.fieldsContainer.innerHTML =
          '<div class="ai-customizer__fields-loading">' +
          "<span>Loading customization options...</span></div>";
      }
      if (this.resultEl) this.resultEl.style.display = "none";
      if (this.noteContainer) this.noteContainer.classList.add("is-hidden");
      this.resultContext = null;
      this.saveToCartProperties("", {});
      this.galleryImages = [];
      if (this.galleryEl) this.galleryEl.style.display = "none";
      this.hideStatus();
      this.fetchConfig();
      this.fetchGallery();
      this.resumeLatestJob();
      this.updateSubmitState();
    }

    bindEvents() {
      const form = this.getSectionEl()?.querySelector('form[data-type="add-to-cart-form"]');
      form?.addEventListener("submit", (event) => {
        if (!this.isLoading) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showStatus("Please wait until your design and photos are ready.", "info");
      }, true);

      if (this.noteInput) {
        this.noteInput.addEventListener("input", () => {
          if (this.isLoading || this.activeJobId) return;
          const context = this.resultContext || {};
          this.saveToCartProperties(
            this.resultImg.src,
            context.fieldValues || this.getDynamicFieldValues(),
            context.portraitUploadIds || this.getReadyUploadIds(),
            context.portraitImageUrls || [],
          );
        });
      }

      this.submitBtn.addEventListener("click", () => this.handleSubmit());
      if (this.cancelBtn) {
        this.cancelBtn.addEventListener("click", () => this.cancelCurrentJob());
      }

      if (this.galleryToggle) {
        this.galleryToggle.addEventListener("click", () => {
          this.galleryOpen = !this.galleryOpen;
          this.galleryGrid.style.display = this.galleryOpen ? "grid" : "none";
          this.galleryToggle.classList.toggle(
            "ai-customizer__gallery-toggle--open",
            this.galleryOpen,
          );
        });
      }
    }

    // ── Dynamic Config ──

    async fetchConfig() {
      if (!this.templateName) {
        this.hideCustomizer();
        return;
      }

      const token = ++this.configFetchToken;

      try {
        const response = await fetch(
          `${this.apiBase}/templates/${encodeURIComponent(this.templateName)}/config`,
          { headers: this.jobHeaders() },
        );

        if (token !== this.configFetchToken) return; // stale, a newer fetch won
        if (!response.ok) {
          console.warn("[AiCustomizer] Config fetch failed:", response.status);
          this.hideCustomizer();
          return;
        }

        const data = unwrapPublicPayload(await response.json());
        if (token !== this.configFetchToken) return;

        if (!data) {
          console.warn("[AiCustomizer] Invalid config response");
          this.hideCustomizer();
          return;
        }

        const fields = data.fields || [];
        const portraitMin = Number.isInteger(data.portrait_min)
          ? data.portrait_min
          : 1;
        const portraitMax = Number.isInteger(data.portrait_max)
          ? data.portrait_max
          : 1;

        // Nothing to render at all → treat as unavailable.
        if (fields.length === 0 && portraitMax === 0) {
          this.hideCustomizer();
          return;
        }

        this.configFields = fields;
        this.portraitMin = portraitMin;
        this.portraitMax = portraitMax;

        this.renderDynamicFields(this.configFields);
        this.renderUploadSlots(this.portraitMax);
        void this.applyRestoredJobInputs();
        this.reveal();
        this.updateSubmitState();

        console.log(
          "[AiCustomizer] Config loaded:",
          fields.length,
          "fields | portraits",
          portraitMin,
          "-",
          portraitMax,
        );
      } catch (error) {
        if (token !== this.configFetchToken) return;
        console.warn("[AiCustomizer] Failed to fetch config:", error);
        this.hideCustomizer();
      }
    }

    reveal() {
      this.container.classList.remove("ai-customizer--gated");
      this.container.classList.remove("ai-customizer--loading");
      this.container.classList.add("ai-customizer--loaded");
    }

    hideCustomizer() {
      this.container.classList.add("ai-customizer--gated");
      this.container.classList.remove("ai-customizer--loaded");
      console.log("[AiCustomizer] Customizer hidden — config unavailable");
    }

    renderDynamicFields(fields) {
      if (!this.fieldsContainer) return;

      this.fieldsContainer.innerHTML = "";

      fields.forEach((field) => {
        const wrapper = document.createElement("div");
        wrapper.className = "ai-customizer__field";

        const label = document.createElement("label");
        label.className = "ai-customizer__field-label";
        label.textContent = field.label;
        label.setAttribute(
          "for",
          `AiCustomizerField-${this.sectionId}-${field.name}`,
        );
        wrapper.appendChild(label);

        let input;
        if (field.type === "dropdown" && field.options) {
          input = document.createElement("select");
          input.className = "ai-customizer__field-select";

          const defaultOpt = document.createElement("option");
          defaultOpt.value = "";
          defaultOpt.textContent = `Select ${field.label.toLowerCase()}...`;
          defaultOpt.disabled = true;
          defaultOpt.selected = true;
          input.appendChild(defaultOpt);

          field.options.forEach((opt) => {
            const option = document.createElement("option");
            option.value = opt.value;
            option.textContent = opt.label;
            input.appendChild(option);
          });
        } else {
          input = document.createElement("input");
          input.type = "text";
          input.className = "ai-customizer__field-input";
          input.placeholder = `Enter ${field.label.toLowerCase()}...`;
        }

        input.id = `AiCustomizerField-${this.sectionId}-${field.name}`;
        input.dataset.fieldName = field.name;
        input.disabled = Boolean(this.isLoading || this.activeJobId);
        const invalidateForInputChange = () => {
          if (this.isLoading || this.activeJobId) return;
          this.invalidateDesign();
          this.updateSubmitState();
        };
        input.addEventListener("input", invalidateForInputChange);
        input.addEventListener("change", invalidateForInputChange);
        wrapper.appendChild(input);

        if (field.type === "dropdown") {
          this.enhanceDropdown(input, label);
        }

        this.fieldsContainer.appendChild(wrapper);
      });
    }

    enhanceDropdown(select, label) {
      if (!select || select.dataset.customSelectReady === "true") return;
      select.dataset.customSelectReady = "true";
      select.classList.add("ai-customizer__field-select--native");

      const shell = document.createElement("div");
      shell.className = "ai-customizer__select-shell";

      const trigger = document.createElement("button");
      trigger.type = "button";
      trigger.id = `${select.id}-trigger`;
      trigger.className = "ai-customizer__select-trigger is-placeholder";
      trigger.setAttribute("aria-haspopup", "listbox");
      trigger.setAttribute("aria-expanded", "false");
      trigger.disabled = select.disabled;

      const triggerValue = document.createElement("span");
      triggerValue.className = "ai-customizer__select-value";
      const triggerIcon = document.createElement("span");
      triggerIcon.className = "ai-customizer__select-icon";
      triggerIcon.setAttribute("aria-hidden", "true");
      triggerIcon.innerHTML =
        '<svg viewBox="0 0 12 8" width="12" height="8"><path d="M1 1.5 6 6.5l5-5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      trigger.appendChild(triggerValue);
      trigger.appendChild(triggerIcon);

      const menu = document.createElement("div");
      menu.id = `${select.id}-listbox`;
      menu.className = "ai-customizer__select-menu";
      menu.setAttribute("role", "listbox");
      menu.setAttribute("aria-labelledby", trigger.id);
      menu.hidden = true;
      trigger.setAttribute("aria-controls", menu.id);

      const optionButtons = [];
      Array.from(select.options).forEach((option) => {
        if (option.disabled && !option.value) return;
        const optionButton = document.createElement("button");
        optionButton.type = "button";
        optionButton.className = "ai-customizer__select-option";
        optionButton.setAttribute("role", "option");
        optionButton.dataset.value = option.value;
        optionButton.textContent = option.textContent;
        optionButton.disabled = option.disabled;
        optionButton.addEventListener("click", () => {
          select.value = option.value;
          syncSelection();
          closeMenu(true);
          select.dispatchEvent(new Event("change", { bubbles: true }));
        });
        menu.appendChild(optionButton);
        optionButtons.push(optionButton);
      });

      const syncSelection = () => {
        const selected = select.options[select.selectedIndex];
        triggerValue.textContent = selected?.textContent || "Select...";
        const hasValue = Boolean(select.value);
        trigger.classList.toggle("is-placeholder", !hasValue);
        optionButtons.forEach((button) => {
          const isSelected = button.dataset.value === select.value;
          button.classList.toggle("is-selected", isSelected);
          button.setAttribute("aria-selected", String(isSelected));
        });
      };

      const closeMenu = (restoreFocus = false) => {
        menu.hidden = true;
        shell.classList.remove("is-open");
        trigger.setAttribute("aria-expanded", "false");
        if (restoreFocus) trigger.focus();
      };

      const openMenu = () => {
        if (trigger.disabled) return;
        this.fieldsContainer
          ?.querySelectorAll?.(".ai-customizer__select-shell.is-open")
          .forEach((openShell) => {
            if (openShell !== shell) openShell.closeCustomSelect?.();
          });
        menu.hidden = false;
        shell.classList.add("is-open");
        trigger.setAttribute("aria-expanded", "true");
      };

      const focusOption = (direction) => {
        const enabled = optionButtons.filter((button) => !button.disabled);
        if (enabled.length === 0) return;
        const activeIndex = enabled.indexOf(document.activeElement);
        const selectedIndex = enabled.findIndex(
          (button) => button.dataset.value === select.value,
        );
        let nextIndex = activeIndex;
        if (direction === "first") nextIndex = 0;
        else if (direction === "last") nextIndex = enabled.length - 1;
        else if (activeIndex < 0) {
          nextIndex = selectedIndex >= 0 ? selectedIndex : 0;
        } else {
          nextIndex =
            (activeIndex + direction + enabled.length) % enabled.length;
        }
        enabled[nextIndex].focus();
      };

      trigger.addEventListener("click", () => {
        if (menu.hidden) openMenu();
        else closeMenu();
      });
      trigger.addEventListener("keydown", (event) => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          openMenu();
          focusOption(event.key === "ArrowDown" ? 1 : -1);
        } else if (event.key === "Escape") {
          closeMenu();
        }
      });
      menu.addEventListener("keydown", (event) => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          focusOption(event.key === "ArrowDown" ? 1 : -1);
        } else if (event.key === "Home" || event.key === "End") {
          event.preventDefault();
          focusOption(event.key === "Home" ? "first" : "last");
        } else if (event.key === "Escape") {
          event.preventDefault();
          closeMenu(true);
        }
      });
      shell.addEventListener("focusout", (event) => {
        if (!shell.contains(event.relatedTarget)) closeMenu();
      });

      select.parentNode.insertBefore(shell, select);
      shell.appendChild(select);
      shell.appendChild(trigger);
      shell.appendChild(menu);
      shell.closeCustomSelect = closeMenu;
      select.customSelectTrigger = trigger;
      select.syncCustomSelect = syncSelection;
      if (label) label.setAttribute("for", trigger.id);
      syncSelection();
    }

    getDynamicFieldValues() {
      const values = {};
      if (!this.fieldsContainer) return values;

      this.fieldsContainer
        .querySelectorAll("[data-field-name]")
        .forEach((el) => {
          const val = el.value.trim();
          if (val) values[el.dataset.fieldName] = val;
        });

      return values;
    }

    areAllFieldsFilled() {
      if (!this.fieldsContainer) return true;
      const fields = this.fieldsContainer.querySelectorAll("[data-field-name]");
      // No fields → nothing to fill (image-only template).
      return Array.from(fields).every((el) => el.value.trim().length > 0);
    }

    // ── Upload Slots (1..N) ──

    renderUploadSlots(count) {
      if (!this.uploadsContainer) return;

      this.uploadsContainer.innerHTML = "";
      this.uploads = [];

      if (!count || count <= 0) {
        // Text-only template — no photo needed.
        if (this.card) this.card.style.display = "none";
        return;
      }

      if (this.card) this.card.style.display = "";

      for (let i = 0; i < count; i++) {
        const slot = this.createUploadSlot(i, count);
        this.uploadsContainer.appendChild(slot.el);
        this.uploads.push(slot);
      }
    }

    createUploadSlot(index, count) {
      const wrap = document.createElement("div");

      if (count > 1) {
        const label = document.createElement("div");
        label.className = "ai-customizer__slot-label";
        label.textContent = `Photo ${index + 1}`;
        wrap.appendChild(label);
      }

      const area = document.createElement("div");
      area.className = "ai-customizer__upload-area";

      const input = document.createElement("input");
      input.type = "file";
      input.className = "ai-customizer__file-input";
      input.accept = UPLOAD_ACCEPT_ATTRIBUTE;

      const placeholder = document.createElement("div");
      placeholder.className = "ai-customizer__upload-placeholder";
      placeholder.innerHTML =
        '<button type="button" class="ai-customizer__choose-btn">' +
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>' +
        "Choose Image</button>" +
        '<span class="ai-customizer__no-file">No file chosen</span>';

      const preview = document.createElement("div");
      preview.className = "ai-customizer__upload-preview";
      preview.style.display = "none";
      const previewImg = document.createElement("img");
      previewImg.alt = "Preview";
      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className = "ai-customizer__remove-btn";
      removeBtn.setAttribute("aria-label", "Remove image");
      removeBtn.innerHTML =
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
      preview.appendChild(previewImg);
      preview.appendChild(removeBtn);

      const uploadIndicator = document.createElement("div");
      uploadIndicator.className = "ai-customizer__upload-indicator";
      uploadIndicator.hidden = true;
      uploadIndicator.setAttribute("role", "status");
      uploadIndicator.innerHTML =
        '<svg class="ai-customizer__spinner" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
        '<path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>' +
        '</svg><span>Uploading photo...</span>';

      area.appendChild(input);
      area.appendChild(placeholder);
      area.appendChild(preview);
      area.appendChild(uploadIndicator);
      wrap.appendChild(area);

      const slot = {
        file: null,
        url: null,
        el: wrap,
        area,
        input,
        placeholder,
        preview,
        previewImg,
        removeBtn,
        uploadIndicator,
        fileName: placeholder.querySelector(".ai-customizer__no-file"),
      };
      input.disabled = Boolean(this.isLoading || this.activeJobId);
      removeBtn.disabled = Boolean(this.isLoading || this.activeJobId);

      input.addEventListener("change", (e) => {
        if (e.target.files && e.target.files[0]) {
          this.handleFile(e.target.files[0], index);
        }
      });
      area.addEventListener("dragover", (e) => {
        e.preventDefault();
        area.classList.add("dragover");
      });
      area.addEventListener("dragleave", () => {
        area.classList.remove("dragover");
      });
      area.addEventListener("drop", (e) => {
        e.preventDefault();
        area.classList.remove("dragover");
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
          this.handleFile(e.dataTransfer.files[0], index);
        }
      });
      removeBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.clearSlot(index);
      });

      return slot;
    }

    setUploadSlotLoading(slot, loading) {
      slot.uploadIndicator.hidden = !loading;
      slot.area.setAttribute("aria-busy", String(loading));
    }

    async handleFile(file, index) {
      if (this.isLoading || this.activeJobId) {
        this.showStatus(
          "Cancel the active design before changing photos.",
          "info",
        );
        return;
      }
      const slot = this.uploads[index];
      if (!slot) return;

      const contentType = this.getUploadContentType(file);
      if (!SUPPORTED_UPLOAD_TYPES.has(contentType)) {
        this.showStatus(
          "Please upload a JPEG, PNG, WebP, HEIC, or HEIF image.",
          "error",
        );
        return;
      }

      const operationId = (slot.operationId || 0) + 1;
      slot.operationId = operationId;
      slot.file = file;
      slot.uploadId = null;
      this.invalidateDesign();
      this.setUploadSlotLoading(slot, true);

      if (HEIC_UPLOAD_TYPES.has(contentType)) {
        slot.previewImg.src = "";
        slot.preview.style.display = "none";
        slot.placeholder.style.display = "flex";
        slot.input.style.display = "block";
        if (slot.fileName) {
          slot.fileName.textContent = `${file.name || "HEIC/HEIF image"} selected (preview loading after upload)`;
        }
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (
            this.uploads[index] !== slot ||
            slot.operationId !== operationId ||
            slot.file !== file
          ) {
            return;
          }
          slot.previewImg.src = e.target.result;
          slot.placeholder.style.display = "none";
          slot.preview.style.display = "flex";
          slot.input.style.display = "none";
        };
        reader.readAsDataURL(file);
      }

      this.showStatus("Uploading your photo...", "info");
      this.submitBtn.disabled = true;

      try {
        const { uploadId, previewUrl } = await this.uploadImage(file);
        if (
          this.uploads[index] !== slot ||
          slot.operationId !== operationId ||
          slot.file !== file
        ) {
          return;
        }
        slot.uploadId = uploadId;
        if (HEIC_UPLOAD_TYPES.has(contentType)) {
          if (previewUrl) {
            slot.previewImg.onerror = () => {
              if (slot.operationId !== operationId) return;
              slot.preview.style.display = "none";
              slot.placeholder.style.display = "flex";
              slot.input.style.display = "block";
              if (slot.fileName) {
                slot.fileName.textContent = `${file.name || "HEIC/HEIF image"} uploaded (preview unavailable)`;
              }
            };
            slot.previewImg.src = previewUrl;
            slot.placeholder.style.display = "none";
            slot.preview.style.display = "flex";
            slot.input.style.display = "none";
          } else if (slot.fileName) {
            slot.fileName.textContent = `${file.name || "HEIC/HEIF image"} uploaded (preview unavailable)`;
          }
        }
        this.storeInputs();
        this.showStatus("Photo accepted and checked successfully!", "success");
      } catch (error) {
        if (
          this.uploads[index] !== slot ||
          slot.operationId !== operationId ||
          slot.file !== file
        ) {
          return;
        }
        this.showStatus(
          error.message || "Failed to upload photo. Please try again.",
          "error",
        );
        this.clearSlot(index);
        return;
      } finally {
        if (
          this.uploads[index] === slot &&
          slot.operationId === operationId &&
          slot.file === file
        ) {
          this.setUploadSlotLoading(slot, false);
        }
      }

      this.updateSubmitState();
    }

    clearSlot(index) {
      if (this.isLoading || this.activeJobId) {
        this.showStatus(
          "Cancel the active design before changing photos.",
          "info",
        );
        return;
      }
      const slot = this.uploads[index];
      if (!slot) return;
      slot.operationId = (slot.operationId || 0) + 1;
      slot.file = null;
      slot.uploadId = null;
      this.setUploadSlotLoading(slot, false);
      slot.input.value = "";
      slot.previewImg.onerror = null;
      slot.previewImg.src = "";
      slot.placeholder.style.display = "flex";
      slot.preview.style.display = "none";
      slot.input.style.display = "block";
      if (slot.fileName) slot.fileName.textContent = "No file chosen";
      // Removing a photo invalidates any design generated from it — drop the
      // stale result and re-sync cart properties so the order never carries a
      // removed design.
      this.invalidateDesign();
      this.updateSubmitState();
    }

    // Clear the generated result + its cart line-item properties.
    invalidateDesign() {
      this.resultContext = null;
      if (this.resultImg) this.resultImg.src = "";
      if (this.resultEl) this.resultEl.style.display = "none";
      if (this.noteContainer) this.noteContainer.classList.add("is-hidden");
      this.saveToCartProperties("", this.getDynamicFieldValues());
      // A user edit starts a new draft. Do not let F5 restore the previous
      // completed job and overwrite the newly selected inputs.
      this.clearStoredJob();
      this.storeInputs();
    }

    getReadyUploadIds() {
      return this.uploads.map((slot) => slot.uploadId).filter(Boolean);
    }

    uploadedCount() {
      return this.getReadyUploadIds().length;
    }

    getUploadContentType(file) {
      const reported = String(file.type || "").trim().toLowerCase();
      if (reported && reported !== "application/octet-stream") return reported;
      const filename = String(file.name || "").toLowerCase();
      if (filename.endsWith(".heic")) return "image/heic";
      if (filename.endsWith(".heif")) return "image/heif";
      return reported;
    }

    hasDynamicFields() {
      if (!this.fieldsContainer) return false;
      return (
        this.fieldsContainer.querySelectorAll("[data-field-name]").length > 0
      );
    }

    updateSubmitState() {
      if (this.isLoading || this.activeJobId) {
        this.submitBtn.disabled = true;
        return;
      }
      if (!this.templateImageUrl) {
        this.submitBtn.disabled = true;
        this.showStatus(MISSING_TEMPLATE_IMAGE_MESSAGE, "error");
        return;
      }
      const fieldsOk = this.areAllFieldsFilled();
      const portraitsOk = this.uploadedCount() >= this.portraitMin;
      // Guard against a degenerate config (no fields AND portrait_min 0)
      // enabling an empty no-op submit: require at least one real input.
      const hasAnyInput = this.hasDynamicFields() || this.uploadedCount() > 0;
      this.submitBtn.disabled = !(fieldsOk && portraitsOk && hasAnyInput);
    }

    // ── UI Helpers ──

    showStatus(message, type, options = {}) {
      this.statusEl.style.display = "block";
      this.statusEl.className = `ai-customizer__status ai-customizer__status--${type}`;
      this.statusEl.querySelector(".ai-customizer__status-text").textContent =
        message;
      if (!options.progress) this.resetJobProgress();
    }

    hideStatus() {
      if (this.statusEl) this.statusEl.style.display = "none";
      this.resetJobProgress();
    }

    resetJobProgress() {
      this.jobProgressValue = 0;
      this.jobProgressInitialEta = null;
      if (this.progressEl) {
        this.progressEl.style.display = "none";
        this.progressEl.setAttribute("aria-valuenow", "0");
      }
      if (this.progressBar) this.progressBar.style.width = "0%";
      if (this.progressValueEl) this.progressValueEl.textContent = "0%";
    }

    updateJobProgress(job) {
      if (!job || !this.progressEl || !this.progressBar) return;
      const eta = Number(job.eta_seconds);
      const hasEta = Number.isFinite(eta) && eta >= 0;
      let target = this.jobProgressValue || 0;

      if (job.status === "pending") {
        target = Math.min(14, Math.max(6, target + 2));
      } else if (job.status === "processing") {
        if (hasEta && !this.jobProgressInitialEta) {
          this.jobProgressInitialEta = Math.max(eta, 1);
        } else if (hasEta) {
          this.jobProgressInitialEta = Math.max(
            this.jobProgressInitialEta,
            eta,
          );
        }
        if (hasEta && this.jobProgressInitialEta) {
          const elapsedRatio = Math.max(
            0,
            Math.min(1, 1 - eta / this.jobProgressInitialEta),
          );
          target = 15 + elapsedRatio * 69;
        } else {
          target = Math.min(84, Math.max(15, target + 2));
        }
      } else if (job.status === "generated") {
        target = 90;
      } else if (
        job.status === "publishing" ||
        job.status === "cancel_requested"
      ) {
        target = 96;
      }

      // Server ETA can move in either direction. Never animate backwards;
      // advance continuously toward the newest estimate instead.
      this.jobProgressValue = Math.min(96, Math.max(target, this.jobProgressValue));
      const rounded = Math.round(this.jobProgressValue);
      this.progressEl.style.display = "grid";
      this.progressEl.setAttribute("aria-valuenow", String(rounded));
      this.progressBar.style.width = `${this.jobProgressValue}%`;
      if (this.progressValueEl) {
        this.progressValueEl.textContent = `${rounded}%`;
      }
    }

    setLoading(loading) {
      this.isLoading = loading;
      this.submitBtn.disabled = loading;
      this.submitText.style.display = loading ? "none" : "inline";
      this.submitLoading.style.display = loading ? "inline-flex" : "none";
      if (this.cancelBtn) {
        this.cancelBtn.style.display =
          loading && this.activeJobId ? "block" : "none";
      }
      if (this.noteInput) this.noteInput.disabled = loading;
      // Keep Previous Designs browsable while a job is running. Individual
      // "Use" actions stay disabled below so browsing cannot replace the
      // active job's inputs or cart context.
      if (this.galleryToggle) this.galleryToggle.disabled = false;
      this.fieldsContainer
        ?.querySelectorAll?.("[data-field-name]")
        .forEach((input) => {
          input.disabled = loading;
          if (input.customSelectTrigger) {
            input.customSelectTrigger.disabled = loading;
            if (loading) {
              input
                .closest?.(".ai-customizer__select-shell")
                ?.closeCustomSelect?.();
            }
          }
        });
      this.uploads.forEach((slot) => {
        if (slot.input) slot.input.disabled = loading;
        if (slot.removeBtn) slot.removeBtn.disabled = loading;
      });
      this.galleryGrid?.querySelectorAll?.("button").forEach((button) => {
        button.disabled = loading;
      });
    }

    formatCountdown(totalSeconds) {
      const mins = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      if (mins > 0) {
        return `${mins} minute${mins !== 1 ? "s" : ""} ${secs} second${secs !== 1 ? "s" : ""}`;
      }
      return `${secs} second${secs !== 1 ? "s" : ""}`;
    }

    showRateLimitPopup(seconds) {
      // Remove existing popup if any
      const existing = document.querySelector(".ai-customizer-rate-popup");
      if (existing) existing.remove();

      const overlay = document.createElement("div");
      overlay.className = "ai-customizer-rate-popup";

      const countdownId = "ai-rate-countdown-" + Date.now();
      overlay.innerHTML = `
        <div class="ai-customizer-rate-popup__box">
          <div class="ai-customizer-rate-popup__icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#e67e22" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <h3 class="ai-customizer-rate-popup__title">Too Many Requests</h3>
          <p class="ai-customizer-rate-popup__message">
            You've reached the maximum number of requests. Please try again in:
          </p>
          <div class="ai-customizer-rate-popup__countdown" id="${countdownId}">
            ${this.formatCountdown(seconds)}
          </div>
          <button class="ai-customizer-rate-popup__close" type="button">Got it</button>
        </div>
      `;

      document.body.appendChild(overlay);

      const countdownEl = document.getElementById(countdownId);
      let remaining = seconds;

      const interval = setInterval(() => {
        remaining--;
        if (remaining <= 0) {
          clearInterval(interval);
          countdownEl.textContent = "You can try again now!";
          countdownEl.classList.add(
            "ai-customizer-rate-popup__countdown--ready",
          );
          return;
        }
        countdownEl.textContent = this.formatCountdown(remaining);
      }, 1000);

      const closeBtn = overlay.querySelector(
        ".ai-customizer-rate-popup__close",
      );
      closeBtn.addEventListener("click", () => {
        clearInterval(interval);
        overlay.remove();
      });
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) {
          clearInterval(interval);
          overlay.remove();
        }
      });
    }

    // ── API ──

    async handleApiResponse(response, fallbackError) {
      if (response.ok) {
        return unwrapPublicPayload(await response.json());
      }

      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error(fallbackError);
      }

      if (
        response.status === 429 ||
        (data && data.message && data.message.includes("Rate limit"))
      ) {
        const match = data.message && data.message.match(/Try again in (\d+)s/);
        const seconds = match ? parseInt(match[1], 10) : 3600;
        this.showRateLimitPopup(seconds);
        throw new Error("Rate limit exceeded. Please wait and try again.");
      }

      throw new Error((data && data.message) || fallbackError);
    }

    async uploadImage(file) {
      const createResponse = await fetch(`${this.apiBase}/uploads`, {
        method: "POST",
        headers: this.jobHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          content_type: this.getUploadContentType(file),
          filename: file.name || null,
        }),
      });

      const created = await this.handleApiResponse(
        createResponse,
        "Failed to upload image. Please try again.",
      );
      const upload = created && created.upload;
      if (!upload || !upload.url || !upload.fields || !created.upload_id) {
        throw new Error("The upload service returned an invalid upload target.");
      }

      const directForm = new FormData();
      Object.entries(upload.fields).forEach(([name, value]) => {
        directForm.append(name, value);
      });
      // Keep the exact original File; no browser-side HEIC/HEIF conversion.
      directForm.append("file", file);
      const directResponse = await fetch(upload.url, {
        method: "POST",
        body: directForm,
      });
      if (!directResponse.ok) {
        throw new Error("Photo transfer failed. Please try again.");
      }

      const ready = await this.waitForUploadReady(created.upload_id);
      return { uploadId: created.upload_id, previewUrl: ready.preview_url || null };
    }

    async waitForUploadReady(uploadId) {
      const deadline = Date.now() + 60 * 1000;
      while (Date.now() < deadline) {
        const response = await fetch(
          `${this.apiBase}/uploads/${encodeURIComponent(uploadId)}`,
          { headers: this.jobHeaders() },
        );
        const result = await this.handleApiResponse(
          response,
          "Could not verify the uploaded photo.",
        );
        const status = result && result.status;
        if (status === "ready") return result;
        if (status === "invalid") {
          throw new Error(
            (result && result.error_code) ||
              "This photo could not be processed.",
          );
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      throw new Error("Photo processing timed out. Please try again.");
    }

    async createRedesignJob(portraitUploadIds, additionalInfo) {
      if (!this.templateImageUrl) {
        throw new Error(MISSING_TEMPLATE_IMAGE_MESSAGE);
      }
      const body = {
        template_image_url: this.templateImageUrl,
        portrait_upload_ids: portraitUploadIds,
        template_name: this.templateName,
        additional_info: additionalInfo,
        session_id: SESSION_ID,
      };
      const storageContext = {
        templateName: this.templateName,
        variantId: this.currentVariantId || null,
        templateImageUrl: this.templateImageUrl || null,
        fieldValues: { ...additionalInfo },
        portraitUploadIds: [...portraitUploadIds],
      };
      const key = this.idempotencyKeyFor(body);
      const response = await fetch(`${this.apiBase}/redesign`, {
        method: "POST",
        headers: this.jobHeaders({
          "Content-Type": "application/json",
          "Idempotency-Key": key,
        }),
        body: JSON.stringify(body),
      });
      const data = await this.handleApiResponse(
        response,
        "Failed to create design job. Please try again.",
      );
      if (!data || !data.job_id) {
        throw new Error("The job service returned an invalid job.");
      }
      return {
        ...data,
        _idempotencyBody: body,
        _storageContext: storageContext,
      };
    }

    idempotencyStorageKey(body) {
      return `ai_customizer_idempotency:${this.productHandle}:${this.templateName}:${JSON.stringify(body)}`;
    }

    idempotencyKeyFor(body) {
      const key = this.idempotencyStorageKey(body);
      let value = localStorage.getItem(key);
      if (!value) {
        value = generateIdempotencyKey();
        localStorage.setItem(key, value);
      }
      return value;
    }

    releaseIdempotencyKey(body) {
      localStorage.removeItem(this.idempotencyStorageKey(body));
    }

    inputsStorageKey() {
      return `ai_customizer_inputs:${this.productHandle}:${this.templateName}`;
    }

    storeInputs(
      fieldValues = this.getDynamicFieldValues(),
      portraitUploadIds = this.getReadyUploadIds(),
    ) {
      if (
        Object.keys(fieldValues).length === 0 &&
        portraitUploadIds.length === 0
      ) {
        this.clearStoredInputs();
        return;
      }
      localStorage.setItem(
        this.inputsStorageKey(),
        JSON.stringify({
          variantId: this.currentVariantId || null,
          templateImageUrl: this.templateImageUrl || null,
          fieldValues,
          portraitUploadIds,
        }),
      );
    }

    readStoredInputs() {
      try {
        const stored = JSON.parse(
          localStorage.getItem(this.inputsStorageKey()) || "null",
        );
        if (!stored) return null;
        if (
          stored.variantId &&
          this.currentVariantId &&
          stored.variantId !== this.currentVariantId
        ) {
          return null;
        }
        if (
          stored.templateImageUrl &&
          this.templateImageUrl &&
          stored.templateImageUrl !== this.templateImageUrl
        ) {
          return null;
        }
        return stored;
      } catch {
        return null;
      }
    }

    clearStoredInputs() {
      localStorage.removeItem(this.inputsStorageKey());
    }

    loadStoredInputsContext() {
      const stored = this.readStoredInputs();
      if (!stored) return;
      this.restoreInputsToken++;
      this.restoredJobContext = {
        jobId: null,
        fieldValues: stored.fieldValues || {},
        portraitUploadIds: Array.isArray(stored.portraitUploadIds)
          ? stored.portraitUploadIds
          : [],
      };
    }

    acknowledgeCreatedJob(job, runId) {
      const context = job && job._storageContext;
      if (
        !job ||
        !context ||
        runId !== this.jobRunId ||
        context.templateName !== this.templateName ||
        context.variantId !== (this.currentVariantId || null) ||
        context.templateImageUrl !== (this.templateImageUrl || null)
      ) {
        return false;
      }
      this.storeJob(job);
      this.clearStoredInputs();
      this.releaseIdempotencyKey(job._idempotencyBody);
      return true;
    }

    jobStorageKey() {
      return `ai_customizer_job:${this.productHandle}:${this.templateName}`;
    }

    legacyJobStorageKey() {
      return `ai_customizer_latest_job:${this.productHandle}:${this.templateName}`;
    }

    storeJob(job) {
      if (!job || !job.job_id) return;
      const suppliedContext = job._storageContext || null;
      const existing = this.readStoredJob();
      const existingContext =
        existing && existing.jobId === job.job_id ? existing : null;
      localStorage.setItem(
        this.jobStorageKey(),
        JSON.stringify({
          jobId: job.job_id,
          variantId: this.currentVariantId || null,
          templateImageUrl: this.templateImageUrl || null,
          fieldValues:
            suppliedContext?.fieldValues || existingContext?.fieldValues || {},
          portraitUploadIds:
            suppliedContext?.portraitUploadIds ||
            existingContext?.portraitUploadIds ||
            [],
        }),
      );
    }

    readStoredJob() {
      try {
        const current = localStorage.getItem(this.jobStorageKey());
        const legacy = localStorage.getItem(this.legacyJobStorageKey());
        const stored = JSON.parse(current || legacy || "null");
        if (!stored) return null;
        if (
          stored.variantId &&
          this.currentVariantId &&
          stored.variantId !== this.currentVariantId
        ) {
          return null;
        }
        if (
          stored.templateImageUrl &&
          this.templateImageUrl &&
          stored.templateImageUrl !== this.templateImageUrl
        ) {
          return null;
        }
        if (!current && legacy) {
          localStorage.setItem(this.jobStorageKey(), JSON.stringify(stored));
          localStorage.removeItem(this.legacyJobStorageKey());
        }
        return stored;
      } catch {
        return null;
      }
    }

    clearStoredJob(jobId) {
      const stored = this.readStoredJob();
      if (!jobId || !stored || stored.jobId === jobId) {
        localStorage.removeItem(this.jobStorageKey());
        localStorage.removeItem(this.legacyJobStorageKey());
      }
    }

    async restoreJobInputs(job, storedContext = null) {
      if (!job || !job.job_id) return;
      const current = this.restoredJobContext;
      if (!current || current.jobId !== job.job_id) {
        this.restoreInputsToken++;
      }
      this.restoredJobContext = {
        jobId: job.job_id,
        fieldValues: storedContext?.fieldValues || {},
        portraitUploadIds: Array.isArray(storedContext?.portraitUploadIds)
          ? storedContext.portraitUploadIds
          : [],
      };
      await this.applyRestoredJobInputs();
    }

    async applyRestoredJobInputs() {
      const context = this.restoredJobContext;
      if (!context) return;
      const token = this.restoreInputsToken;

      this.fieldsContainer
        ?.querySelectorAll?.("[data-field-name]")
        .forEach((input) => {
          if (Object.prototype.hasOwnProperty.call(
            context.fieldValues,
            input.dataset.fieldName,
          )) {
            input.value = String(context.fieldValues[input.dataset.fieldName]);
            input.syncCustomSelect?.();
          }
        });

      await Promise.all(
        context.portraitUploadIds.map(async (uploadId, index) => {
          const slot = this.uploads[index];
          if (!slot) return;
          slot.uploadId = uploadId;
          slot.file = null;
          if (slot.restoredUploadId === uploadId && slot.previewImg.src) return;
          slot.restoredUploadId = uploadId;
          if (slot.fileName) slot.fileName.textContent = "Restoring photo...";

          try {
            const response = await fetch(
              `${this.apiBase}/uploads/${encodeURIComponent(uploadId)}`,
              { headers: this.jobHeaders() },
            );
            const result = await this.handleApiResponse(
              response,
              "Could not restore the uploaded photo.",
            );
            if (
              token !== this.restoreInputsToken ||
              this.restoredJobContext !== context ||
              this.uploads[index] !== slot ||
              slot.restoredUploadId !== uploadId
            ) {
              return;
            }
            const upload = result;
            if (upload?.status === "ready" && upload.preview_url) {
              slot.previewImg.src = upload.preview_url;
              slot.placeholder.style.display = "none";
              slot.preview.style.display = "flex";
              slot.input.style.display = "none";
              return;
            }
            if (slot.fileName) slot.fileName.textContent = "Uploaded photo";
          } catch (error) {
            if (
              token === this.restoreInputsToken &&
              this.restoredJobContext === context &&
              slot.fileName
            ) {
              slot.fileName.textContent = "Uploaded photo";
              console.warn("[AiCustomizer] Failed to restore photo preview:", error);
            }
          }
        }),
      );
      this.updateSubmitState();
    }

    async pollRedesignJob(jobId, storedContext = null) {
      const token = this.jobPollToken;
      const deadline = Date.now() + JOB_POLL_TIMEOUT_MS;
      while (Date.now() < deadline) {
        if (token !== this.jobPollToken) throw new JobPollingStoppedError();
        const response = await fetch(
          `${this.apiBase}/redesign/${encodeURIComponent(jobId)}`,
          { headers: this.jobHeaders() },
        );
        const result = await this.handleApiResponse(
          response,
          "Could not retrieve the design job.",
        );
        if (token !== this.jobPollToken) throw new JobPollingStoppedError();
        const job = result;
        if (!job || !job.status) throw new Error("The job service returned an invalid status.");
        this.storeJob(job);
        if (this.restoringJobId === jobId) {
          await this.restoreJobInputs(job, storedContext);
        }
        this.showJobStatus(job);
        if (job.status === "completed") {
          const ids = job.portrait_upload_ids || [];
          if (ids.length && (job.portrait_image_urls || []).length !== ids.length) {
            const prepared = await fetch(`${this.apiBase}/redesign/${jobId}/portraits`, {
              method: "POST", headers: this.jobHeaders(),
            });
            const result = await this.handleApiResponse(prepared, "Could not prepare your photos.");
            if (token !== this.jobPollToken) throw new JobPollingStoppedError();
            return result;
          }
          return job;
        }
        if (job.status === "failed" || job.status === "canceled") {
          this.clearStoredJob(jobId);
          throw new Error(job.status === "canceled" ? "Design generation was canceled." : "Design generation failed. Please ensure that your input image is suitable and try again.");
        }
        if (job.status === "publish_uncertain") {
          throw new Error("Publishing needs confirmation. Please contact support with this design job.");
        }
        await new Promise((resolve) => setTimeout(resolve, JOB_POLL_INTERVAL_MS));
      }
      throw new Error("Design generation timed out. Please check again later.");
    }

    showJobStatus(job) {
      const messages = {
        pending: "Your design is queued.",
        processing: "Generating your custom design...",
        generated: "Design generated. Preparing publication...",
        publishing: "Publishing your design...",
        cancel_requested: "Cancellation requested. The active step must finish first.",
      };
      if (!messages[job.status]) return;
      this.showStatus(messages[job.status], "info", { progress: true });
      this.updateJobProgress(job);
    }

    showCompletedJob(job, context = {}) {
      const stored = this.readStoredJob();
      const storedContext =
        stored && stored.jobId === job.job_id ? stored : null;
      const fieldValues =
        context.fieldValues || storedContext?.fieldValues || {};
      const portraitUploadIds =
        job.portrait_upload_ids || context.portraitUploadIds || storedContext?.portraitUploadIds || [];
      const redesignedUrl = job.output_url;
      if (!redesignedUrl) throw new Error("Completed job has no published design URL.");
      this.resultImg.src = redesignedUrl;
      this.resultEl.style.display = "block";
      const portraitImageUrls = job.portrait_image_urls || [];
      this.resultContext = { fieldValues, portraitUploadIds, portraitImageUrls };
      this.clearStoredInputs();
      this.addImageToGallery(redesignedUrl);
      if (this.noteContainer) this.noteContainer.classList.remove("is-hidden");
      this.saveToCartProperties(redesignedUrl, fieldValues, portraitUploadIds, portraitImageUrls);
      if (!this.galleryImages.some((image) => image.id === job.job_id)) {
        this.addNewToGallery({
          id: job.job_id,
          output_url: redesignedUrl,
          template_name: this.templateName,
          additional_info: fieldValues,
          portrait_upload_ids: portraitUploadIds,
          portrait_image_urls: portraitImageUrls,
          created_at: new Date().toISOString(),
        });
      }
      this.showStatus("Your custom design has been generated!", "success");
    }

    async resumeLatestJob() {
      // A saved draft represents a user edit made after the last generated
      // design. It must win even if an older job key is still present (for
      // example after a tab was closed between two localStorage updates).
      if (this.readStoredInputs()) return;
      const stored = this.readStoredJob();
      if (!stored || !stored.jobId || this.activeJobId) return;
      const runId = ++this.jobRunId;
      this.activeJobId = stored.jobId;
      this.restoringJobId = stored.jobId;
      this.setLoading(true);
      this.showStatus("Restoring your latest design job...", "info");
      try {
        const job = await this.pollRedesignJob(stored.jobId, stored);
        if (runId !== this.jobRunId) return;
        this.showCompletedJob(job, {
          fieldValues: stored.fieldValues || {},
          portraitUploadIds: stored.portraitUploadIds || [],
        });
      } catch (error) {
        if (runId === this.jobRunId && !(error instanceof JobPollingStoppedError)) {
          this.showStatus(error.message || "Could not restore the latest design job.", "error");
        }
      } finally {
        if (runId === this.jobRunId && this.activeJobId === stored.jobId) {
          this.activeJobId = null;
          this.restoringJobId = null;
          this.setLoading(false);
          this.updateSubmitState();
        }
      }
    }

    async cancelCurrentJob() {
      const jobId = this.activeJobId;
      if (!jobId) return;
      const runId = ++this.jobRunId;
      this.jobPollToken++;
      let keepPolling = false;
      try {
        const response = await fetch(
          `${this.apiBase}/redesign/${encodeURIComponent(jobId)}`,
          { method: "DELETE", headers: this.jobHeaders() },
        );
        const result = await this.handleApiResponse(response, "Failed to cancel this design job.");
        if (runId !== this.jobRunId) return;
        const job = result;
        if (job && job.status === "canceled") {
          this.clearStoredJob(jobId);
          this.showStatus("Generation canceled. You can change the inputs and try again.", "info");
        } else if (job && job.status === "completed") {
          this.storeJob(job);
          this.showCompletedJob(job);
          this.showStatus("This design finished before it could be canceled.", "info");
        } else if (job && job.status === "cancel_requested") {
          this.storeJob(job);
          this.showStatus("Cancellation requested. The active step must finish first.", "info");
          keepPolling = true;
          this.pollRedesignJob(jobId)
            .then((completed) => {
              if (runId === this.jobRunId) this.showCompletedJob(completed);
            })
            .catch((error) => {
              if (runId === this.jobRunId && !(error instanceof JobPollingStoppedError)) {
                this.showStatus(error.message || "Could not finish cancellation.", "error");
              }
            })
            .finally(() => {
              if (runId === this.jobRunId) {
                if (this.activeJobId === jobId) this.activeJobId = null;
                this.setLoading(false);
                this.updateSubmitState();
              }
            });
        } else {
          this.showStatus("This design is already in a terminal state.", "info");
        }
      } catch (error) {
        if (runId === this.jobRunId) {
          this.showStatus(error.message || "Failed to cancel this job.", "error");
        }
      } finally {
        if (!keepPolling && runId === this.jobRunId) {
          if (this.activeJobId === jobId) this.activeJobId = null;
          this.setLoading(false);
          this.updateSubmitState();
        }
      }
    }

    // ── Cart Properties ──

    saveToCartProperties(
      designUrl,
      fieldValues,
      portraitUploadIds = this.getReadyUploadIds(),
      portraitImageUrls = [],
    ) {
      const sectionEl = this.getSectionEl();
      if (!sectionEl) return;

      const form = sectionEl.querySelector(
        'form[data-type="add-to-cart-form"]',
      );
      if (!form) return;

      const designInput = sectionEl.querySelector(
        `#ai-custom-design-url-${this.sectionId}`,
      );
      const portraitInput = sectionEl.querySelector(
        `#ai-portrait-url-${this.sectionId}`,
      );

      const uploadIds = portraitUploadIds;

      if (designInput) designInput.value = designUrl;
      // Only durable Shopify URLs from the selected design belong in orders.
      if (portraitInput) portraitInput.value = portraitImageUrls[0] || "";

      form
        .querySelectorAll(
          ".ai-portrait-upload-id-prop, .ai-portrait-extra-prop",
        )
        .forEach((el) => el.remove());
      portraitImageUrls.slice(1).forEach((url, i) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = `properties[Portrait Image ${i + 2}]`;
        input.value = url;
        input.className = "ai-portrait-extra-prop";
        form.appendChild(input);
      });
      uploadIds.forEach((uploadId, i) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = `properties[Portrait Upload ID ${i + 1}]`;
        input.value = uploadId;
        input.className = "ai-portrait-upload-id-prop";
        form.appendChild(input);
      });

      // Remove old dynamic property inputs
      form
        .querySelectorAll(".ai-custom-dynamic-prop")
        .forEach((el) => el.remove());

      // Create hidden inputs for each dynamic field value
      for (const [name, value] of Object.entries(fieldValues)) {
        const field = this.configFields.find((f) => f.name === name);
        const label = field ? field.label : name;

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = `properties[${label}]`;
        input.value = value;
        input.className = "ai-custom-dynamic-prop";
        form.appendChild(input);
      }
      if (this.noteInput) {
        const noteValue = this.noteInput.value.trim();
        form
          .querySelectorAll(".ai-custom-note-prop")
          .forEach((el) => el.remove());

        if (noteValue) {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = "properties[Design Notes]";
          input.value = noteValue;
          input.className = "ai-custom-note-prop";
          form.appendChild(input);
        }
      }

      console.log("[AiCustomizer] Saved to cart properties:", {
        designUrl,
        portraitUploadCount: uploadIds.length,
        fields: fieldValues,
      });
    }

    // ── Gallery ──

    addImageToGallery(imageUrl) {
      const mainSwiper = this.getMainSwiper();
      const subSwiper = this.getSubSwiper();

      if (!mainSwiper) {
        console.warn(
          "[AiCustomizer] Main swiper not found, cannot add to gallery",
        );
        return;
      }

      // Remove previous custom slide if exists
      const sectionEl = this.getSectionEl();
      sectionEl
        .querySelectorAll('[data-ai-custom="true"]')
        .forEach((el) => el.remove());

      // Create main slide HTML
      const mainSlideHtml = `
        <div class="product__media-item swiper-slide product__media-item--contain" data-ai-custom="true">
          <div class="product__media global-media-settings">
            <img src="${imageUrl}" loading="eager" alt="Custom design" style="width:100%;height:auto;">
          </div>
        </div>
      `;

      // Prepend to main swiper and slide to it
      mainSwiper.prependSlide(mainSlideHtml);
      mainSwiper.slideTo(0, 300);

      // Also add thumbnail to sub swiper
      if (subSwiper) {
        const subSlideHtml = `
          <div class="product__media-subitem swiper-slide product__media-item--contain" data-ai-custom="true">
            <div class="product__media">
              <img src="${imageUrl}" loading="lazy" alt="Custom design" style="width:100%;height:auto;">
            </div>
          </div>
        `;
        subSwiper.prependSlide(subSlideHtml);
        subSwiper.slideTo(0, 300);
      }

      console.log("[AiCustomizer] Image added to gallery via Swiper API");
    }

    // ── Gallery (Previous Designs) ──

    async fetchGallery() {
      if (!this.templateName) return;

      const token = ++this.galleryFetchToken;

      try {
        const params = new URLSearchParams({
          session_id: SESSION_ID,
          template_name: this.templateName,
          limit: "50",
        });

        const response = await fetch(`${this.apiBase}/gallery?${params}`, {
          headers: this.jobHeaders(),
        });
        if (token !== this.galleryFetchToken) return;
        if (!response.ok) return;

        const result = unwrapPublicPayload(await response.json());
        if (token !== this.galleryFetchToken) return;
        if (!result || !result.images) return;

        this.galleryImages = result.images;
        if (this.galleryImages.length > 0) {
          this.renderGallery();
        }
      } catch (error) {
        console.warn("[AiCustomizer] Failed to fetch gallery:", error);
      }
    }

    renderGallery() {
      if (!this.galleryEl || this.galleryImages.length === 0) return;

      this.galleryEl.style.display = "block";
      this.galleryCount.textContent = this.galleryImages.length;
      this.galleryGrid.innerHTML = "";

      this.galleryImages.forEach((img) => {
        const item = document.createElement("div");
        item.className = "ai-customizer__gallery-item";
        item.dataset.imageId = img.id;

        const date = new Date(img.created_at);
        const dateStr = date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });

        item.innerHTML = `
          <div class="ai-customizer__gallery-img-wrap">
            <img src="${img.output_url}" alt="Previous design" loading="lazy">
          </div>
          <div class="ai-customizer__gallery-item-footer">
            <span class="ai-customizer__gallery-date">${dateStr}</span>
            <button type="button" class="ai-customizer__gallery-use-btn">Use</button>
          </div>
        `;

        const useBtn = item.querySelector(".ai-customizer__gallery-use-btn");
        useBtn.disabled = Boolean(this.isLoading || this.activeJobId);
        useBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.selectGalleryImage(img);
        });

        // Click on image to preview in swiper
        const imgEl = item.querySelector("img");
        imgEl.addEventListener("click", () => {
          this.addImageToGallery(img.output_url);
        });

        this.galleryGrid.appendChild(item);
      });
    }

    async selectGalleryImage(img) {
      if (this.isLoading || this.activeJobId) {
        this.showStatus(
          "Cancel the active design before selecting a saved design.",
          "info",
        );
        return;
      }
      const uploadIds = img.portrait_upload_ids || [];
      if (uploadIds.length && (img.portrait_image_urls || []).length !== uploadIds.length) {
        const runId = this.jobRunId;
        this.setLoading(true);
        this.showStatus("Preparing photos for this saved design...", "info");
        try {
          const response = await fetch(`${this.apiBase}/redesign/${img.id}/portraits`, {
            method: "POST",
            headers: this.jobHeaders(),
          });
          const result = await this.handleApiResponse(response, "Could not prepare your photos.");
          if (runId !== this.jobRunId) return;
          if (!Array.isArray(result.portrait_image_urls) || result.portrait_image_urls.length !== uploadIds.length) {
            throw new Error("Your saved design's photos are not ready. Please try again.");
          }
          img.portrait_image_urls = result.portrait_image_urls;
        } catch (error) {
          if (runId === this.jobRunId) this.showStatus(error.message, "error");
          return;
        } finally {
          if (runId === this.jobRunId) this.setLoading(false);
        }
      }
      // Inject into product swiper
      this.addImageToGallery(img.output_url);

      // Show result area
      this.resultImg.src = img.output_url;
      this.resultEl.style.display = "block";

      // Show notes field
      if (this.noteContainer) {
        this.noteContainer.classList.remove("is-hidden");
      }

      // Save to cart properties
      const fieldValues = img.additional_info || {};
      const portraitUploadIds = Array.isArray(img.portrait_upload_ids)
        ? img.portrait_upload_ids
        : [];
      const portraitImageUrls = img.portrait_image_urls || [];
      this.resultContext = { fieldValues, portraitUploadIds, portraitImageUrls };
      this.clearStoredInputs();
      this.saveToCartProperties(img.output_url, fieldValues, portraitUploadIds, portraitImageUrls);

      this.showStatus("Design selected from your gallery!", "success");

      // Highlight selected item
      this.galleryGrid
        .querySelectorAll(".ai-customizer__gallery-item")
        .forEach((el) =>
          el.classList.remove("ai-customizer__gallery-item--selected"),
        );
      const selectedItem = this.galleryGrid.querySelector(
        `[data-image-id="${img.id}"]`,
      );
      if (selectedItem) {
        selectedItem.classList.add("ai-customizer__gallery-item--selected");
      }
    }

    addNewToGallery(imageData) {
      this.galleryImages.unshift(imageData);
      this.renderGallery();
    }

    // ── Submit ──

    async handleSubmit() {
      if (this.isLoading || this.activeJobId) return;
      if (!this.templateImageUrl) {
        this.showStatus(MISSING_TEMPLATE_IMAGE_MESSAGE, "error");
        return;
      }
      const fieldValues = this.getDynamicFieldValues();
      if (!this.areAllFieldsFilled()) return;
      if (this.uploadedCount() < this.portraitMin) return;

      const runId = ++this.jobRunId;
      const portraitUploadIds = this.getReadyUploadIds();
      this.restoredJobContext = null;
      this.restoreInputsToken++;
      this.restoringJobId = null;

      console.log(
        "[AiCustomizer] Submitting with template:",
        this.templateName,
        "| portraits:",
        portraitUploadIds.length,
      );

      this.setLoading(true);
      this.hideStatus();
      this.invalidateDesign();

      let submittedJobId = null;
      try {
        this.showStatus(
          "Generating your custom design... This may take a moment.",
          "info",
        );
        const job = await this.createRedesignJob(
          portraitUploadIds,
          fieldValues,
        );
        if (runId !== this.jobRunId) return;
        if (!this.acknowledgeCreatedJob(job, runId)) return;
        const context = { portraitUploadIds, fieldValues };
        this.activeJobId = job.job_id;
        submittedJobId = job.job_id;
        this.setLoading(true);
        const completed = await this.pollRedesignJob(job.job_id);
        if (runId !== this.jobRunId) return;
        this.showCompletedJob(completed, context);
      } catch (error) {
        if (runId === this.jobRunId && !(error instanceof JobPollingStoppedError)) {
          this.showStatus(error.message || "Something went wrong. Please try again.", "error");
        }
      } finally {
        if (
          runId === this.jobRunId &&
          (!submittedJobId || this.activeJobId === submittedJobId)
        ) {
          this.activeJobId = null;
          this.setLoading(false);
          this.updateSubmitState();
        }
      }
    }
  }

  function init() {
    document.querySelectorAll(".ai-customizer").forEach((el) => {
      if (!el.dataset.initialized) {
        new AiCustomizer(el);
        el.dataset.initialized = "true";
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
